import { RANKS_ASC, RANKS_DESC, SUITS, cardPairToIndex, cardToId } from './cards'

type Suitedness = 'pair' | 'suited' | 'offsuit' | 'all' | 'specific'

interface RangeDescriptor {
  rank1: number
  rank2: number
  suitedness: Suitedness
  suit1?: number
  suit2?: number
}

export interface ParsedRange {
  raw: Float32Array
  matrix: number[]
  liveCombos: number
  totalWeight: number
}

export class RangeParseError extends Error {
  constructor(message: string, public token?: string) {
    super(message)
    this.name = 'RangeParseError'
  }
}

function parseDescriptor(value: string): RangeDescriptor {
  const specific = value.match(/^([2-9TJQKA])([cdhs])([2-9TJQKA])([cdhs])$/i)
  if (specific) {
    const rank1 = RANKS_ASC.indexOf(specific[1].toUpperCase())
    const rank2 = RANKS_ASC.indexOf(specific[3].toUpperCase())
    const suit1 = SUITS.indexOf(specific[2].toLowerCase())
    const suit2 = SUITS.indexOf(specific[4].toLowerCase())
    if (rank1 < rank2) throw new RangeParseError('個別コンボは高いランクを先に書いてください', value)
    if (rank1 === rank2 && suit1 === suit2) throw new RangeParseError('同じカードは2回使えません', value)
    return { rank1, rank2, suitedness: 'specific', suit1, suit2 }
  }

  const group = value.match(/^([2-9TJQKA])([2-9TJQKA])([so]?)$/i)
  if (!group) throw new RangeParseError('レンジ表記を読み取れません', value)
  const rank1 = RANKS_ASC.indexOf(group[1].toUpperCase())
  const rank2 = RANKS_ASC.indexOf(group[2].toUpperCase())
  if (rank1 < rank2) throw new RangeParseError('高いランクを先に書いてください', value)
  if (rank1 === rank2 && group[3]) throw new RangeParseError('ペアにs/oは付けられません', value)
  const suitedness: Suitedness = rank1 === rank2 ? 'pair' : group[3] === 's' ? 'suited' : group[3] === 'o' ? 'offsuit' : 'all'
  return { rank1, rank2, suitedness }
}

function indicesFor(desc: RangeDescriptor): number[] {
  const result: number[] = []
  if (desc.suitedness === 'specific') {
    result.push(cardPairToIndex(desc.rank1 * 4 + desc.suit1!, desc.rank2 * 4 + desc.suit2!))
    return result
  }
  if (desc.suitedness === 'pair') {
    for (let suit1 = 0; suit1 < 4; suit1++) {
      for (let suit2 = suit1 + 1; suit2 < 4; suit2++) {
        result.push(cardPairToIndex(desc.rank1 * 4 + suit1, desc.rank1 * 4 + suit2))
      }
    }
    return result
  }
  for (let suit1 = 0; suit1 < 4; suit1++) {
    for (let suit2 = 0; suit2 < 4; suit2++) {
      const suited = suit1 === suit2
      if (desc.suitedness === 'suited' && !suited) continue
      if (desc.suitedness === 'offsuit' && suited) continue
      result.push(cardPairToIndex(desc.rank1 * 4 + suit1, desc.rank2 * 4 + suit2))
    }
  }
  return result
}

function expandToken(token: string): RangeDescriptor[] {
  if (token.endsWith('+')) {
    const base = parseDescriptor(token.slice(0, -1))
    if (base.suitedness === 'specific') throw new RangeParseError('個別コンボに+は使えません', token)
    const gap = base.rank1 - base.rank2
    const result: RangeDescriptor[] = []
    if (gap <= 1) {
      for (let rank = base.rank1; rank < 13; rank++) {
        result.push({ ...base, rank1: rank, rank2: rank - gap })
      }
    } else {
      for (let low = base.rank2; low < base.rank1; low++) result.push({ ...base, rank2: low })
    }
    return result
  }

  if (token.includes('-')) {
    const parts = token.split('-')
    if (parts.length !== 2) throw new RangeParseError('ダッシュ範囲が不正です', token)
    const start = parseDescriptor(parts[0])
    const end = parseDescriptor(parts[1])
    if (start.suitedness !== end.suitedness || start.suitedness === 'specific') {
      throw new RangeParseError('ダッシュ範囲のs/oを揃えてください', token)
    }
    const gap1 = start.rank1 - start.rank2
    const gap2 = end.rank1 - end.rank2
    const result: RangeDescriptor[] = []
    if (gap1 === gap2 && start.rank1 > end.rank1) {
      for (let rank = end.rank1; rank <= start.rank1; rank++) {
        result.push({ ...start, rank1: rank, rank2: rank - gap1 })
      }
      return result
    }
    if (start.rank1 === end.rank1 && start.rank2 > end.rank2) {
      for (let low = end.rank2; low <= start.rank2; low++) result.push({ ...start, rank2: low })
      return result
    }
    throw new RangeParseError('ダッシュ範囲は強い側から弱い側へ書いてください', token)
  }

  return [parseDescriptor(token)]
}

function average(raw: Float32Array, indices: number[]): number {
  return indices.reduce((sum, index) => sum + raw[index], 0) / indices.length
}

export function matrixCellLabel(row: number, col: number): string {
  if (row === col) return `${RANKS_DESC[row]}${RANKS_DESC[col]}`
  if (row < col) return `${RANKS_DESC[row]}${RANKS_DESC[col]}s`
  return `${RANKS_DESC[col]}${RANKS_DESC[row]}o`
}

export function rawToMatrix(raw: Float32Array): number[] {
  const matrix: number[] = []
  for (let row = 0; row < 13; row++) {
    for (let col = 0; col < 13; col++) {
      matrix.push(average(raw, indicesFor(parseDescriptor(matrixCellLabel(row, col)))))
    }
  }
  return matrix
}

export function parseRange(notation: string, board: string[] = []): ParsedRange {
  const raw = new Float32Array((52 * 51) / 2)
  const tokens = notation
    .replace(/\s+/g, '')
    .split(',')
    .filter(Boolean)

  if (tokens.length === 0) throw new RangeParseError('レンジが空です')

  for (const original of [...tokens].reverse()) {
    const [rangeToken, weightToken, extra] = original.split(':')
    if (extra !== undefined) throw new RangeParseError('ウェイト指定が不正です', original)
    const weight = weightToken === undefined ? 1 : Number(weightToken)
    if (!Number.isFinite(weight) || weight < 0 || weight > 1) {
      throw new RangeParseError('ウェイトは0〜1で指定してください', original)
    }
    for (const descriptor of expandToken(rangeToken)) {
      for (const index of indicesFor(descriptor)) raw[index] = weight
    }
  }

  const dead = new Set(board.map(cardToId))
  let liveCombos = 0
  let totalWeight = 0
  for (let card1 = 0; card1 < 52; card1++) {
    for (let card2 = card1 + 1; card2 < 52; card2++) {
      const weight = raw[cardPairToIndex(card1, card2)]
      totalWeight += weight
      if (!dead.has(card1) && !dead.has(card2) && weight > 0) liveCombos += weight
    }
  }
  if (liveCombos === 0) throw new RangeParseError('ボード除外後の有効コンボがありません')

  return { raw, matrix: rawToMatrix(raw), liveCombos, totalWeight }
}

export function setMatrixCell(notation: string, row: number, col: number, weight: number): string {
  const label = matrixCellLabel(row, col)
  const cleanWeight = Math.max(0, Math.min(1, weight))
  return `${label}:${cleanWeight},${notation}`
}

export function compactRangeSummary(notation: string, board: string[] = []): string {
  const parsed = parseRange(notation, board)
  return `${parsed.liveCombos.toFixed(parsed.liveCombos % 1 ? 1 : 0)} combos`
}
